import { useMemo } from "react";
import { useSelector } from "react-redux";
import { STORYBOARD_TYPES } from "@/utils/constants";
import type { Storyboard } from "@/utils/types";
import { useActiveProject, useCanvasId } from "@/utils/utils";
import { getFileKeyForStoryboard } from "../IframePool/utils";
import { useIframePoolStore } from "../IframePool/iframePoolStore";

export const useStoryboardUrl = (storyboard?: Storyboard) => {
  const canvasId = useCanvasId();
  const project = useActiveProject();
  const canvasIdToDevServerUrl = useSelector(
    (state: any) => state.iframe.canvasIdToDevServerUrl,
  );
  const devServerUrl = canvasIdToDevServerUrl[canvasId];

  const baseStoryboardUrl = useMemo(() => {
    if (!storyboard) {
      return "";
    }

    if (!devServerUrl) {
      return "";
    }

    let url: string;
    if (storyboard.type === STORYBOARD_TYPES.APPLICATION) {
      url = `${devServerUrl}${storyboard.route ?? ""}`;
    } else if (!storyboard.created) {
      return "";
    } else {
      const fileKey = getFileKeyForStoryboard(storyboard, canvasId);
      url = `${devServerUrl}/tempobook?storyboard=true&type=${storyboard.type}&initFileKey=${fileKey}&storyboardId=${storyboard.id}`;
    }

    const parsedUrl = new URL(url);
    if (import.meta.env.VITE_DEBUG_LOG_DEVTOOLS) {
      parsedUrl.searchParams.set("debugLog", "true");
    }
    if (project?.framework) {
      parsedUrl.searchParams.set("framework", project?.framework);
    }

    return parsedUrl.toString();
  }, [devServerUrl, project, storyboard, canvasId]);

  return baseStoryboardUrl;
};

export const useIframePoolUrl = (overrideIframeId?: string) => {
  const canvasId = useCanvasId();
  const devServerUrl = useSelector(
    (state: any) => state.iframe.canvasIdToDevServerUrl?.[canvasId],
  );
  const project = useActiveProject();
  const tempobookIframeId = useIframePoolStore(
    (state) => state.tempobookIframe?.id,
  );
  const iframeId = overrideIframeId || tempobookIframeId;

  return useMemo(() => {
    if (!devServerUrl || !iframeId) {
      return "";
    }

    const parsedUrl = new URL(
      `${devServerUrl}/tempobook?id=${iframeId}&iframeId=${iframeId}`,
    );
    if (import.meta.env.VITE_DEBUG_LOG_DEVTOOLS) {
      parsedUrl.searchParams.set("debugLog", "true");
    }
    if (project?.framework) {
      parsedUrl.searchParams.set("framework", project?.framework);
    }

    return parsedUrl.toString();
  }, [devServerUrl, project, iframeId]);
};
